import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getFullDate,
  isCustomerView,
  linkURl,
  statusColor,
  timeSince
} from 'utils'

import { ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Badge,
  Flex,
  Icon,
  IconButton,
  Link,
  Portal,
  Stack,
  Tag,
  TagLabel,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { Menu, MenuItem, MenuList } from '@chakra-ui/react'

import CvssTag from 'components/Misc/CvssTag'
import EpssTag from 'components/Misc/EpssTag'
import ExternalLink from 'components/Misc/ExternalLink'
import LynkAction from 'components/Misc/LynkAction'
import SeverityTag from 'components/Misc/SeverityTag'

import useCustomToast from 'hooks/useCustomToast'
import { useGlobalQueryContext } from 'hooks/useGlobalQueryContext'
import { useHasPermission } from 'hooks/useHasPermission'
import { usePartsContext } from 'hooks/usePartsContext'
import { useThemeColor } from 'hooks/useThemeColors'

import { BsCircleHalf } from 'react-icons/bs'
import { FaGlobe, FaHouseUser, FaLightbulb, FaSitemap } from 'react-icons/fa'
import { FaBookOpen, FaBullhorn, FaLink, FaListCheck } from 'react-icons/fa6'

const VulnerabilityColumns = (
  editVulns,
  isArchived,
  setActiveRow,
  jiraConnection,
  LINK,
  JIRA,
  VULN
) => {
  const partsContext = usePartsContext()
  const { primaryBlueText, primaryTextColor } = useThemeColor([
    'primaryBlueText',
    'primaryTextColor'
  ])
  const { isFreeTier } = useGlobalQueryContext()
  const customerView = isCustomerView()

  const { showToast } = useCustomToast()

  const navigate = useNavigate()
  const updateCon = useHasPermission({
    parentKey: 'view_connections',
    childKey: 'create_update_connection'
  })

  return useMemo(() => {
    const onGlobalView = (id, vuln) => {
      localStorage.setItem('activeVuln', vuln)
      navigate(
        `/vendor/vulnerabilities?tab=productVulnerabilities&vulnId=${id}`
      )
    }
    const handleJiraTicket = (row) => {
      const issueTracker = row?.externalUrls?.some(
        (item) => item?.name === 'issue-tracker' && item?.url !== ''
      )
      if (issueTracker) {
        showToast({
          status: 'error',
          title: 'Issue already exists'
        })
      } else {
        if (jiraConnection?.enabled === true) {
          setActiveRow(row)
          JIRA.onOpen()
        } else {
          showToast({
            status: 'error',
            title: 'Jira Configuration not set.',
            description:
              'Please configure Jira connections in the Organization Settings -> Connections.'
          })
        }
      }
    }
    const handleEditVuln = (row) => {
      setActiveRow(row)
      VULN.onOpen()
    }
    const onCheck = (item) => (item ? primaryBlueText : primaryTextColor)
    const columns = [
      // CVE ID
      {
        id: 'VULNS_VULN_ID',
        name: 'ID',
        wrap: true,
        selector: (row, index) => {
          const { vuln, isPart, component } = row
          const { sbom } = component
          const { project } = sbom
          const { id, vulnInfo } = vuln
          const { kev } = vulnInfo ? vulnInfo : ''
          return (
            <Flex direction='row' alignItems={'flex-start'} gap={2} my={3}>
              <Tooltip label={vuln.source === 'osv' ? 'OSV View' : 'NVD View'}>
                <Link
                  href={linkURl(vuln.source, vuln.vulnId)}
                  target={'_blank'}
                >
                  <Icon
                    as={ExternalLinkIcon}
                    onClick={() => partsContext.push()}
                    sx={{ w: '16px', h: '16px', color: primaryBlueText }}
                  />
                </Link>
              </Tooltip>
              <Stack direction={'column'} spacing={1.5}>
                <Text
                  fontSize='sm'
                  id={`vuln${index}`}
                  color={customerView ? primaryTextColor : primaryBlueText}
                  data-tag='allowRowEvents'
                  onClick={() => {
                    if (!customerView) {
                      onGlobalView(id, vuln.vulnId)
                    }
                  }}
                >
                  {vuln.vulnId !== null ? `${vuln.vulnId}` : ''}
                </Text>
                {isPart && (
                  <Text
                    fontWeight={'medium'}
                    sx={{
                      w: 'fit-content',
                      fontSize: 'xs',
                      color: primaryTextColor
                    }}
                  >
                    {project?.projectGroup?.name || ''} :{' '}
                    {component?.name || ''}
                  </Text>
                )}
                {kev === true && (
                  <Badge
                    width={'fit-content'}
                    variant='subtle'
                    colorScheme='red'
                  >
                    KEV
                  </Badge>
                )}
              </Stack>
            </Flex>
          )
        },
        width: '20%',
        sortable: true
      },
      // SEVERITY
      {
        id: 'VULNS_SEV',
        name: 'SEVERITY',
        selector: (row) => <SeverityTag value={row?.vuln?.sev} />,
        width: '10%',
        sortable: true,
        wrap: true
      },
      // SOURCE
      {
        id: 'VULNS_SOURCE',
        name: 'SOURCE',
        selector: (row) => {
          const { vuln } = row
          return (
            <Tag
              onClick={(e) => {
                e.currentTarget.parentElement.click()
              }}
              size='sm'
              key='md'
              variant='solid'
              textTransform={'uppercase'}
              colorScheme={vuln.source === 'osv' ? 'red' : 'blue'}
              sx={{
                w: '100%',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <TagLabel>{vuln.source}</TagLabel>
            </Tag>
          )
        },
        width: '8.3%',
        sortable: true,
        wrap: true
      },
      // CVSS
      {
        id: 'VULNS_CVSS_SCORE',
        name: 'CVSS',
        selector: (row) => <CvssTag value={row?.vuln?.cvssScore} />,
        width: '7%',
        sortable: true,
        wrap: true
      },
      // EPSS
      {
        id: 'VULN_INFOS_EPSS_SCORES',
        name: 'EPSS',
        selector: (row) => {
          const { vuln } = row
          const { vulnInfo } = vuln
          const { epssScores } = vulnInfo ? vulnInfo : ''
          return <EpssTag value={epssScores} />
        },
        width: '10%',
        sortable: true,
        wrap: true
      },
      // STATUS
      {
        id: 'VEX_STATUSES_NAME',
        name: 'STATUS',
        selector: (row) => {
          const { vuln, vexStatus, isComplete } = row
          return (
            <Tag
              size='md'
              variant='solid'
              data-testid={`vexStatus-${vuln?.vulnId}`}
              width={customerView ? '130px' : '160px'}
              onClick={(e) => {
                e.currentTarget.parentElement.click()
              }}
              colorScheme={statusColor(
                vexStatus ? vexStatus.name : 'Unspecified'
              )}
            >
              <TagLabel mx={'auto'} as={Flex} gap={2} alignItems='center'>
                {!customerView && isComplete === false && <BsCircleHalf />}{' '}
                {vexStatus !== null ? vexStatus.name : 'Unspecified'}
              </TagLabel>
            </Tag>
          )
        },
        width: !customerView && '13%',
        wrap: true,
        sortable: true
      },
      // UPDATED AT
      {
        id: 'COMPONENT_VULNS_UPDATED_AT',
        name: 'UPDATED',
        selector: (row) => (
          <Tooltip label={getFullDate(row.vuln.updatedAt)} placement={'top'}>
            <Text
              color={primaryTextColor}
              onClick={(e) => {
                e.currentTarget.parentElement.click()
              }}
              textAlign={'right'}
            >
              {timeSince(row.vuln.updatedAt)}
            </Text>
          </Tooltip>
        ),
        sortable: true,
        sortFunction: (a, b) => {
          const dateA = new Date(a.vuln.updatedAt)
          const dateB = new Date(b.vuln.updatedAt)
          return dateA - dateB // Sort in descending order
        },
        width: '10%',
        wrap: true,
        right: 'true'
      },
      // ACTION
      {
        id: 'action',
        name: 'ACTION',
        selector: (row) => {
          const { isPart, currentExternalUrls, externalUrls, vuln } = row
          const advisories = isPart
            ? currentExternalUrls?.find((item) => item.name === 'advisories')
            : externalUrls?.find((item) => item.name === 'advisories')
          const documentation = isPart
            ? currentExternalUrls?.find((item) => item.name === 'documentation')
            : externalUrls?.find((item) => item.name === 'documentation')
          const other = isPart
            ? currentExternalUrls?.find((item) => item.name === 'other')
            : externalUrls?.find((item) => item.name === 'other')
          const issueTracker = isPart
            ? currentExternalUrls?.find((item) => item.name === 'issue-tracker')
            : externalUrls?.find((item) => item.name === 'issue-tracker')
          return (
            <Flex alignItems={'center'} gap={2}>
              {/* advisories */}
              <ExternalLink
                link={advisories}
                icon={<FaBullhorn color={onCheck(advisories)} fontSize={16} />}
              />
              {/* documentation */}
              <ExternalLink
                link={documentation}
                icon={
                  <FaBookOpen color={onCheck(documentation)} fontSize={16} />
                }
              />
              {/* other */}
              <ExternalLink
                link={other}
                icon={<FaLink color={onCheck(other)} fontSize={16} />}
              />
              <ExternalLink
                link={issueTracker}
                icon={
                  <FaListCheck color={onCheck(issueTracker)} fontSize={16} />
                }
              />
              <Menu>
                <LynkAction aria-label='Options' data-testid='vuln-actions' />
                <Portal>
                  <MenuList fontSize='sm'>
                    {vuln?.source === 'custom' && (
                      <MenuItem
                        hidden={isFreeTier}
                        isDisabled={!updateCon}
                        onClick={() => handleEditVuln(row)}
                      >
                        Edit Vulnerability
                      </MenuItem>
                    )}
                    <MenuItem
                      data-testid='edit_vuln_links'
                      isDisabled={!editVulns}
                      onClick={() => {
                        setActiveRow(row)
                        LINK.onOpen()
                      }}
                    >
                      Edit Links
                    </MenuItem>
                    <MenuItem
                      hidden={isFreeTier}
                      isDisabled={!updateCon}
                      onClick={() => handleJiraTicket(row)}
                    >
                      Create Jira Ticket
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </Flex>
          )
        },
        wrap: true,
        right: 'true',
        omit: customerView || isArchived ? true : false
      },
      {
        id: '',
        name: '',
        right: true,
        selector: (row) => {
          const { externalUrls, currentExternalUrls } = row
          const website =
            externalUrls?.find((item) => item.name === 'website') ||
            currentExternalUrls?.find((item) => item.name === 'website')
          const distribution =
            externalUrls?.find((item) => item.name === 'distribution') ||
            currentExternalUrls?.find((item) => item.name === 'distribution')
          const issueTracker =
            externalUrls?.find((item) => item.name === 'issue-tracker') ||
            currentExternalUrls?.find((item) => item.name === 'issue-tracker')
          const vcs =
            externalUrls?.find((item) => item.name === 'vcs') ||
            currentExternalUrls?.find((item) => item.name === 'vcs')

          return (
            <Stack direction={'row'} alignItems={'center'}>
              {/* WEBSITE */}
              <Tooltip placement='top' label={website?.url}>
                <Link href={website?.url} isExternal>
                  <IconButton
                    type='button'
                    size='xs'
                    variant='solid'
                    isDisabled={!website}
                    colorScheme='gray'
                    icon={<FaGlobe color={primaryTextColor} fontSize={16} />}
                  />
                </Link>
              </Tooltip>
              {/* DISTRIBUTION */}
              <Tooltip placement='top' label={vcs?.url}>
                <Link href={vcs?.url} isExternal>
                  <IconButton
                    type='button'
                    size='xs'
                    variant='solid'
                    colorScheme='gray'
                    isDisabled={!vcs}
                    icon={<FaSitemap color={primaryTextColor} fontSize={16} />}
                  />
                </Link>
              </Tooltip>
              {/* ADVISORIES */}
              <Tooltip placement='top' label={issueTracker?.url}>
                <Link href={issueTracker?.url} isExternal>
                  <IconButton
                    type='button'
                    size='xs'
                    variant='solid'
                    colorScheme='gray'
                    isDisabled={!issueTracker}
                    icon={
                      <FaHouseUser color={primaryTextColor} fontSize={16} />
                    }
                  />
                </Link>
              </Tooltip>
              {/* SUPPORT */}
              <Tooltip placement='top' label={distribution?.url}>
                <Link href={distribution?.url} isExternal>
                  <IconButton
                    type='button'
                    size='xs'
                    variant='solid'
                    isDisabled={!distribution}
                    colorScheme='gray'
                    icon={
                      <FaLightbulb color={primaryTextColor} fontSize={16} />
                    }
                  />
                </Link>
              </Tooltip>
            </Stack>
          )
        },
        omit: !customerView ? true : false
      }
    ]
    return columns
  }, [
    customerView,
    isArchived,
    navigate,
    showToast,
    jiraConnection?.enabled,
    setActiveRow,
    JIRA,
    VULN,
    primaryBlueText,
    primaryTextColor,
    partsContext,
    isFreeTier,
    updateCon,
    editVulns,
    LINK
  ])
}

export default VulnerabilityColumns
